/**
 * WhiteboardRevealOverlay — the post-commit spectacle.
 *
 * A full-screen layer that plays after a whiteboard import commits (mounted by
 * WhiteboardImportModal on the 'done' step). Purely presentational: it renders
 * a RevealScript built by lib/whiteboardReveal from already-committed state and
 * never writes anything.
 *
 * Three views over the same script:
 *  • auto        — the fog-lift: the capture photo appears, the fog lifts, the
 *                  graph materializes at the photo's positions, edges draw,
 *                  altitude strata land, counts tick up. Click/Esc skips.
 *  • walkthrough — "play what happened": the same surface stepped through the
 *                  script's narrated scenes (← → keys or buttons).
 *  • diagram     — the imported structure restaged in the Peak Priorities
 *                  visual grammar (1020×700 dark canvas, altitude strata,
 *                  grammar footer) with SVG / PNG export.
 *
 * Honors prefers-reduced-motion by rendering the finished state immediately.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Play,
  Sparkles,
  RotateCcw,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Download,
  Layers,
  Link2,
  Copy,
  Check,
  ListOrdered,
} from 'lucide-react';
import type { RevealScript, RevealNode, RevealStep } from '../../../../lib/whiteboardReveal';
import { livingGraphConfigFromScript } from '../../../../lib/whiteboardLivingGraph';
import { sessionPrefillFromReveal, type SessionPrefillOrigin } from '../../../../lib/sessionPrefill';

export type RevealView = 'auto' | 'walkthrough' | 'diagram';

interface Props {
  script: RevealScript;
  /** Which face to open on. The user can move between them inside the overlay. */
  mode: RevealView;
  onClose: () => void;
  /** Where this reveal came from — stamps the session-prefill provenance. */
  origin?: SessionPrefillOrigin;
}

// ── Peak Priorities tokens (dark canvas) ─────────────────────────────────────

const CANVAS = '#0B0F19';
const TEXT_PRI = '#D8DDE8';
const TEXT_SEC = '#9BA5BC';
const TEXT_TER = '#6B7A96';
const EDGE_INK = '#6B7A96';

const CATEGORY_TOKEN: Record<string, { stroke: string; fill: string; label: string }> = {
  problem: { stroke: '#E08A6D', fill: 'rgba(192,83,58,0.22)', label: 'Problem' },
  risk: { stroke: '#E06868', fill: 'rgba(200,64,64,0.22)', label: 'Risk' },
  opportunity: { stroke: '#6FBF8F', fill: 'rgba(58,125,86,0.24)', label: 'Opportunity' },
  solution: { stroke: '#8EA7D8', fill: 'rgba(61,75,111,0.38)', label: 'Solution' },
  commitment: { stroke: '#E3BE55', fill: 'rgba(212,160,23,0.20)', label: 'Commitment' },
  investment: { stroke: '#B48FD8', fill: 'rgba(124,86,168,0.24)', label: 'Investment' },
};
const DEFAULT_TOKEN = { stroke: '#9BA5BC', fill: 'rgba(155,165,188,0.14)', label: '' };

function tokenFor(node: RevealNode) {
  return (node.itemCategory && CATEGORY_TOKEN[node.itemCategory]) || DEFAULT_TOKEN;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

// ── Reveal timeline (auto view) ──────────────────────────────────────────────

const T_PHOTO = 250; // photo fades in from black
const T_FOG = 2000; // fog lifts: photo dims to a ghost, title lands
const T_NODES = 2900; // first node materializes
const NODE_STAGGER = 70;
const EDGE_STAGGER = 90;
const NODE_ANIM = 420;
const EDGE_ANIM = 650;

/** The reveal's phase milestones, derived from how much there is to show. */
function buildTimeline(nodeCount: number, edgeCount: number) {
  const edgesAt = T_NODES + nodeCount * NODE_STAGGER + NODE_ANIM + 250;
  const strataAt = edgesAt + edgeCount * EDGE_STAGGER + EDGE_ANIM + 250;
  const endAt = strataAt + 900;
  return { edgesAt, strataAt, endAt };
}

const PHASE_END = 6;

// ── Small pieces ─────────────────────────────────────────────────────────────

/** rAF count-up for the summary chips (mirrors the DiffReveal idiom). */
function CountUp({ target, durationMs = 700 }: { target: number; durationMs?: number }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target === 0) {
      setN(0);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      setN(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return <>{n}</>;
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'whiteboard'
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Word wrap for the diagram's node labels: up to `maxLines`, ellipsized. */
function wrapLabel(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    if (!line) line = w;
    else if ((line + ' ' + w).length <= maxChars) line += ' ' + w;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1].slice(0, maxChars - 1) + '…';
    return kept;
  }
  return lines;
}

/** Pull an edge's endpoints back from the node centers so lines meet the boxes. */
function trim(x1: number, y1: number, x2: number, y2: number, by: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = (dx / len) * by;
  const uy = (dy / len) * by;
  return { x1: x1 + ux, y1: y1 + uy, x2: x2 - ux, y2: y2 - uy };
}

// ── Diagram scene (Peak Priorities grammar, exportable) ─────────────────────

const DIAG_W = 1020;
const DIAG_H = 700;
const PLOT = { x: 160, y: 135, w: 810, h: 445 };
const NODE_W = 152;
const NODE_H = 46;

const EDGE_DASH: Partial<Record<string, string>> = {
  prerequisite: '7 5',
  related: '2 4',
  elaboration: '7 5',
};

function DiagramScene({ script, svgRef }: { script: RevealScript; svgRef: React.RefObject<SVGSVGElement | null> }) {
  const pos = new Map(script.nodes.map((n) => [n.itemId, n.position] as const));
  const px = (x: number) => PLOT.x + x * PLOT.w;
  const py = (y: number) => PLOT.y + y * PLOT.h;
  const categories = [...new Set(script.nodes.map((n) => n.itemCategory).filter(Boolean))] as string[];

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${DIAG_W} ${DIAG_H}`}
      className="max-w-full max-h-full"
      role="img"
      aria-label={`Diagram of ${script.boardTitle}`}
    >
      <rect width={DIAG_W} height={DIAG_H} fill={CANVAS} />

      {/* Header */}
      <text x={60} y={62} fontSize={30} fill={TEXT_PRI} fontFamily="fieldwork, 'Work Sans', system-ui, sans-serif" fontWeight={600}>
        {script.boardTitle}
      </text>
      <text x={60} y={88} fontSize={13} fill={TEXT_SEC} fontFamily="'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif">
        Captured from a whiteboard · {script.nodes.length} items · {script.edges.length} relationships
      </text>

      {/* Altitude strata (5 bands, strategic at the top) */}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i}>
          <rect
            x={PLOT.x - 20}
            y={115 + i * 97}
            width={PLOT.w + 40}
            height={97}
            fill={i % 2 === 0 ? 'rgba(61,75,111,0.10)' : 'rgba(61,75,111,0.04)'}
          />
          <text x={118} y={115 + i * 97 + 54} fontSize={13} fill={TEXT_TER} fontFamily="'JetBrains Mono', monospace" textAnchor="middle">
            {5 - i}
          </text>
        </g>
      ))}
      <text x={118} y={106} fontSize={11} fill={TEXT_TER} fontFamily="'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif" textAnchor="middle">
        Strategic ↑
      </text>
      <text x={118} y={614} fontSize={11} fill={TEXT_TER} fontFamily="'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif" textAnchor="middle">
        Tactical ↓
      </text>

      {/* Edges */}
      <defs>
        <marker id="wbrv-diag-arrow" markerWidth={7} markerHeight={7} refX={6} refY={3.5} orient="auto">
          <path d="M0,0 L7,3.5 L0,7 Z" fill={EDGE_INK} />
        </marker>
      </defs>
      {script.edges.map((e, i) => {
        const s = pos.get(e.sourceItemId);
        const t = pos.get(e.targetItemId);
        if (!s || !t) return null;
        const seg = trim(px(s.x), py(s.y), px(t.x), py(t.y), NODE_W / 2 - 10);
        return (
          <line
            key={i}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={EDGE_INK}
            strokeWidth={1.5}
            strokeDasharray={EDGE_DASH[e.type]}
            markerEnd={e.directionality === 'directed' ? 'url(#wbrv-diag-arrow)' : undefined}
          />
        );
      })}

      {/* Nodes */}
      {script.nodes.map((n) => {
        const tok = tokenFor(n);
        const cx = px(n.position.x);
        const cy = py(n.position.y);
        const lines = wrapLabel(n.title, 22, 2);
        return (
          <g key={n.itemId}>
            <rect
              x={cx - NODE_W / 2}
              y={cy - NODE_H / 2}
              width={NODE_W}
              height={NODE_H}
              rx={8}
              fill={tok.fill}
              stroke={tok.stroke}
              strokeWidth={1.5}
              strokeDasharray={n.kind === 'reused' ? '4 3' : undefined}
            />
            {lines.map((line, li) => (
              <text
                key={li}
                x={cx}
                y={cy + (li - (lines.length - 1) / 2) * 15 + 4}
                fontSize={11.5}
                fill="#E8EDF6"
                textAnchor="middle"
                fontFamily="'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif"
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}

      {/* Grammar footer */}
      <line x1={60} y1={624} x2={DIAG_W - 60} y2={624} stroke="rgba(155,165,188,0.25)" strokeWidth={1} />
      <text x={60} y={650} fontSize={11.5} fill={TEXT_TER} fontFamily="'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif">
        Box = Item · Color = category · Arrow = depends on · Dashed border = reused item · Height = altitude
      </text>
      {categories.length > 0 && (
        <text x={60} y={672} fontSize={11.5} fill={TEXT_TER} fontFamily="'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif">
          {categories.map((c) => CATEGORY_TOKEN[c]?.label ?? c).join(' · ')}
        </text>
      )}
      <text x={DIAG_W - 60} y={650} fontSize={11.5} fill={TEXT_SEC} textAnchor="end" fontFamily="Spectral, Georgia, serif" fontStyle="italic">
        Peak Priorities
      </text>
    </svg>
  );
}

// ── The overlay ──────────────────────────────────────────────────────────────

export function WhiteboardRevealOverlay({ script, mode, onClose, origin = 'whiteboard_import' }: Props) {
  const navigate = useNavigate();
  const [view, setView] = useState<RevealView>(mode);

  // The wall-to-plan seam (SPEC-04): carry the revealed items — real platform
  // ids — straight into the session wizard as router state.
  const rankInSession = useCallback(() => {
    navigate('/prioritization/sessions/new', {
      state: { prefill: sessionPrefillFromReveal(script, origin) },
    });
  }, [navigate, script, origin]);
  const [stepIdx, setStepIdx] = useState(0);
  // instant = render the finished state with no entrance animations (skip,
  // reduced motion, or any non-auto view).
  const [instant, setInstant] = useState(() => mode !== 'auto' || prefersReducedMotion());
  const [phase, setPhase] = useState(() => (mode !== 'auto' || prefersReducedMotion() ? PHASE_END : 0));
  const [runId, setRunId] = useState(0);
  const timersRef = useRef<number[]>([]);
  const diagramSvgRef = useRef<SVGSVGElement | null>(null);

  const timeline = useMemo(() => buildTimeline(script.nodes.length, script.edges.length), [script]);

  // Drive the auto timeline. Re-runs on Replay (runId bump).
  useEffect(() => {
    if (view !== 'auto' || instant) return;
    const at = (ms: number, p: number) => timersRef.current.push(window.setTimeout(() => setPhase(p), ms));
    at(T_PHOTO, 1);
    at(T_FOG, 2);
    at(T_NODES, 3);
    at(timeline.edgesAt, 4);
    at(timeline.strataAt, 5);
    at(timeline.endAt, PHASE_END);
    return () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
  }, [view, instant, runId, timeline]);

  const skip = useCallback(() => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
    setInstant(true);
    setPhase(PHASE_END);
  }, []);

  const replay = useCallback(() => {
    if (prefersReducedMotion()) return;
    setView('auto');
    setPhase(0);
    setInstant(false);
    setRunId((r) => r + 1);
  }, []);

  const step: RevealStep | null = view === 'walkthrough' ? (script.steps[stepIdx] ?? null) : null;
  const nextStep = useCallback(
    () => setStepIdx((i) => Math.min(i + 1, script.steps.length - 1)),
    [script.steps.length],
  );
  const prevStep = useCallback(() => setStepIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (view === 'auto' && phase < PHASE_END) skip();
        else onClose();
      } else if (view === 'walkthrough' && (e.key === 'ArrowRight' || e.key === ' ')) {
        e.preventDefault();
        nextStep();
      } else if (view === 'walkthrough' && e.key === 'ArrowLeft') {
        prevStep();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, phase, skip, onClose, nextStep, prevStep]);

  // ── Layer visibility, unified across auto phases and walkthrough steps ────
  const done = phase >= PHASE_END;
  let photoOpacity = 0;
  let showNodes = false;
  let showEdges = false;
  let showStrata = false;
  let showChips = false;
  if (view === 'auto') {
    photoOpacity = phase === 0 ? 0 : phase === 1 ? 0.9 : 0.1;
    showNodes = phase >= 3;
    showEdges = phase >= 4;
    showStrata = phase >= 5;
    showChips = phase >= 5;
  } else if (view === 'walkthrough' && step) {
    photoOpacity = step.id === 'photo' ? 0.9 : step.id === 'recognition' ? 0.3 : 0.08;
    showNodes = step.id !== 'photo';
    showEdges = step.id !== 'photo' && step.id !== 'recognition';
    showStrata = step.id === 'structure' || step.id === 'home' || step.id === 'next';
    showChips = step.id === 'home' || step.id === 'next';
  }

  const highlightNodes = step?.highlight?.nodeIds ? new Set(step.highlight.nodeIds) : null;
  const highlightEdges = step?.highlight?.edgeIdx ? new Set(step.highlight.edgeIdx) : null;
  const nodeDim = (n: RevealNode) => (highlightNodes && !highlightNodes.has(n.itemId) ? 0.18 : 1);
  const edgeDim = (i: number) =>
    highlightEdges ? (highlightEdges.has(i) ? 1 : 0.15) : highlightNodes ? 0.25 : 1;

  const posOf = useMemo(() => new Map(script.nodes.map((n) => [n.itemId, n.position] as const)), [script.nodes]);

  const exportSvg = useCallback(() => {
    const svg = diagramSvgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    downloadBlob(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }), `${slugify(script.boardTitle)}-diagram.svg`);
  }, [script.boardTitle]);

  // The board as a Visual Language 'living-graph' config — paste-able into
  // the /visual-language/ viewer's config editor (select Living Graph → edit).
  const [copiedConfig, setCopiedConfig] = useState(false);
  const copyVlConfig = useCallback(async () => {
    const json = JSON.stringify(livingGraphConfigFromScript(script), null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopiedConfig(true);
      window.setTimeout(() => setCopiedConfig(false), 2000);
    } catch {
      /* clipboard unavailable (permissions / non-secure context) */
    }
  }, [script]);

  const exportPng = useCallback(() => {
    const svg = diagramSvgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = DIAG_W * 2;
      canvas.height = DIAG_H * 2;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) downloadBlob(blob, `${slugify(script.boardTitle)}-diagram.png`);
        }, 'image/png');
      }
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [script.boardTitle]);

  const ctaBtn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors border-white/20 text-[#D8DDE8] hover:border-white/50 hover:bg-[var(--bg-raised)]/5';

  return (
    <div
      className="fixed inset-0 z-[70] overflow-hidden"
      style={{ background: CANVAS }}
      role="dialog"
      aria-label={`Import reveal: ${script.boardTitle}`}
      onClick={() => {
        if (view === 'auto' && phase < PHASE_END) skip();
      }}
    >
      <style>{`
        @keyframes wbrv-pop { from { opacity: 0; transform: translate(-50%, -50%) scale(0.55); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
        @keyframes wbrv-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wbrv-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes wbrv-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        @keyframes wbrv-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(227,190,85,0); } 50% { box-shadow: 0 0 0 6px rgba(227,190,85,0.25); } }
        .wbrv-node { animation: wbrv-pop ${NODE_ANIM}ms cubic-bezier(0.2, 0.9, 0.3, 1.15) both; }
        .wbrv-edge { stroke-dasharray: 1; animation: wbrv-draw ${EDGE_ANIM}ms ease-out both; }
        .wbrv-fade { animation: wbrv-fade 700ms ease-out both; }
        .wbrv-rise { animation: wbrv-rise 600ms ease-out both; }
        .wbrv-changed { animation: wbrv-pop ${NODE_ANIM}ms cubic-bezier(0.2, 0.9, 0.3, 1.15) both, wbrv-pulse 1.6s ease-in-out 2; }
        .wbrv-instant .wbrv-node, .wbrv-instant .wbrv-changed { animation: none; opacity: 1; transform: translate(-50%, -50%); }
        .wbrv-instant .wbrv-fade, .wbrv-instant .wbrv-rise { animation: none; opacity: 1; transform: none; }
        .wbrv-instant .wbrv-edge { animation: none; stroke-dasharray: none; }
      `}</style>

      <div className={instant ? 'wbrv-instant' : undefined}>
        {/* Capture photo — the wall itself, then a ghost under the graph. */}
        {script.photoUrl && (
          <img
            src={script.photoUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-contain transition-all duration-1000"
            style={{ opacity: photoOpacity, filter: photoOpacity < 0.3 ? 'blur(2px) saturate(0.6)' : 'none' }}
          />
        )}

        {/* Title — lands as the fog lifts. */}
        {(view !== 'auto' || phase >= 2) && (
          <div className="absolute top-[5%] inset-x-0 text-center wbrv-fade pointer-events-none">
            <p className="text-2xl md:text-3xl" style={{ color: TEXT_PRI, fontFamily: "Spectral, Georgia, serif" }}>
              {script.boardTitle}
            </p>
            <p className="text-xs mt-1 tracking-wide uppercase" style={{ color: TEXT_TER }}>
              from marker ink to living graph
            </p>
          </div>
        )}

        {/* The board surface: strata, edges, nodes at their photo positions. */}
        <div className="absolute left-[6%] right-[6%] top-[15%] bottom-[19%]">
          {showStrata && (
            <div className="absolute inset-0 wbrv-fade pointer-events-none" aria-hidden>
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="absolute inset-x-0"
                  style={{
                    top: `${i * 20}%`,
                    height: '20%',
                    background: i % 2 === 0 ? 'rgba(61,75,111,0.12)' : 'rgba(61,75,111,0.05)',
                  }}
                />
              ))}
              <span className="absolute top-1 left-2 text-[11px] uppercase tracking-wider" style={{ color: TEXT_TER }}>
                Strategic ↑
              </span>
              <span className="absolute bottom-1 left-2 text-[11px] uppercase tracking-wider" style={{ color: TEXT_TER }}>
                Tactical ↓
              </span>
            </div>
          )}

          {showEdges && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              <defs>
                <marker id="wbrv-arrow" markerWidth={6} markerHeight={6} refX={5} refY={3} orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L6,3 L0,6 Z" fill={EDGE_INK} />
                </marker>
              </defs>
              {script.edges.map((e, i) => {
                const s = posOf.get(e.sourceItemId);
                const t = posOf.get(e.targetItemId);
                if (!s || !t) return null;
                return (
                  <line
                    key={i}
                    className="wbrv-edge"
                    pathLength={1}
                    x1={s.x * 100}
                    y1={s.y * 100}
                    x2={t.x * 100}
                    y2={t.y * 100}
                    stroke={EDGE_INK}
                    strokeWidth={0.45}
                    vectorEffect="non-scaling-stroke"
                    markerEnd={e.directionality === 'directed' ? 'url(#wbrv-arrow)' : undefined}
                    style={{ animationDelay: `${i * EDGE_STAGGER}ms`, opacity: edgeDim(i), transition: 'opacity 300ms' }}
                  >
                    <title>{e.type}</title>
                  </line>
                );
              })}
            </svg>
          )}

          {showNodes &&
            script.nodes.map((n, i) => {
              const tok = tokenFor(n);
              return (
                <div
                  key={n.itemId}
                  className={`absolute -translate-x-1/2 -translate-y-1/2  px-3 py-1.5 max-w-[17%] text-center ${
                    n.kind === 'changed' ? 'wbrv-changed' : 'wbrv-node'
                  }`}
                  style={{
                    left: `${n.position.x * 100}%`,
                    top: `${n.position.y * 100}%`,
                    background: tok.fill,
                    border: `1.5px ${n.kind === 'reused' ? 'dashed' : 'solid'} ${tok.stroke}`,
                    animationDelay: `${i * NODE_STAGGER}ms`,
                    opacity: nodeDim(n),
                    transition: 'opacity 300ms',
                  }}
                  title={`${n.title}${n.itemCategory ? ` · ${n.itemCategory}` : ''}${n.altitudeLevel ? ` · altitude ${n.altitudeLevel}` : ''}`}
                >
                  <span className="block text-[13px] font-medium leading-tight break-words" style={{ color: '#E8EDF6' }}>
                    {n.kind === 'reused' && <Link2 className="inline h-3 w-3 mr-1 -mt-0.5" style={{ color: tok.stroke }} />}
                    {n.title}
                  </span>
                </div>
              );
            })}
        </div>

        {/* Summary chips — the counts land as the structure settles. */}
        {showChips && (
          <div className="absolute bottom-[11%] inset-x-0 flex flex-wrap items-center justify-center gap-2 wbrv-rise pointer-events-none">
            {([
              { value: script.summary.created, label: script.summary.created === 1 ? 'item created' : 'items created' },
              ...(script.summary.reused > 0 ? [{ value: script.summary.reused, label: 'reused' }] : []),
              ...(script.summary.changed ? [{ value: script.summary.changed, label: 'changed' }] : []),
              { value: script.summary.relationships, label: script.summary.relationships === 1 ? 'relationship' : 'relationships' },
            ] as Array<{ value: number; label: string }>).map((c) => (
              <span
                key={c.label}
                className="px-3 py-1 rounded-full border border-white/15 text-sm tabular-nums"
                style={{ color: TEXT_PRI, background: 'rgba(255,255,255,0.04)' }}
              >
                <strong className="font-semibold">{instant ? c.value : <CountUp target={c.value} />}</strong> {c.label}
              </span>
            ))}
            {script.summary.collection && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/15 text-sm"
                style={{ color: TEXT_PRI, background: 'rgba(255,255,255,0.04)' }}
              >
                <Layers className="h-3.5 w-3.5" style={{ color: TEXT_SEC }} /> “{script.summary.collection}”
              </span>
            )}
          </div>
        )}
      </div>

      {/* Diagram view sits above the board surface. */}
      {view === 'diagram' && (
        <div className="absolute inset-0 flex flex-col" style={{ background: CANVAS }} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-center flex-1 min-h-0 p-6 pt-14">
            <DiagramScene script={script} svgRef={diagramSvgRef} />
          </div>
        </div>
      )}

      {/* Walkthrough narration bar. */}
      {view === 'walkthrough' && step && (
        <div className="absolute bottom-0 inset-x-0 pb-5 px-4 flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <p
            className="max-w-2xl text-center text-base md:text-lg leading-relaxed"
            style={{ color: TEXT_PRI, fontFamily: "Spectral, Georgia, serif" }}
            aria-live="polite"
          >
            {step.narration}
          </p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={prevStep} disabled={stepIdx === 0} className={`${ctaBtn} disabled:opacity-30`} aria-label="Previous step">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs tabular-nums" style={{ color: TEXT_TER }}>
              {stepIdx + 1} / {script.steps.length}
            </span>
            {stepIdx < script.steps.length - 1 ? (
              <button type="button" onClick={nextStep} className={ctaBtn} aria-label="Next step">
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <>
                {script.nodes.length > 0 && (
                  <button
                    type="button"
                    onClick={rankInSession}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-[var(--bg-raised)]/90 text-[var(--text-pri)] hover:bg-[var(--bg-raised)]"
                  >
                    <ListOrdered className="h-4 w-4" /> Rank these in a session
                  </button>
                )}
                <button type="button" onClick={onClose} className={ctaBtn}>
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* End-state CTA row (auto view). */}
      {view === 'auto' && done && (
        <div className="absolute bottom-[3.5%] inset-x-0 flex flex-wrap items-center justify-center gap-2 wbrv-rise" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={replay} className={ctaBtn}>
            <RotateCcw className="h-4 w-4" /> Replay
          </button>
          <button
            type="button"
            onClick={() => {
              setStepIdx(0);
              setInstant(true);
              setView('walkthrough');
            }}
            className={ctaBtn}
          >
            <Play className="h-4 w-4" /> Play what happened
          </button>
          <button type="button" onClick={() => { setInstant(true); setView('diagram'); }} className={ctaBtn}>
            <LayoutGrid className="h-4 w-4" /> View diagram
          </button>
          {script.nodes.length > 0 && (
            <button
              type="button"
              onClick={rankInSession}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-[var(--bg-raised)]/90 text-[var(--text-pri)] hover:bg-[var(--bg-raised)]"
              title="Open the session wizard with these items pre-selected"
            >
              <ListOrdered className="h-4 w-4" /> Rank these in a session
            </button>
          )}
          <button type="button" onClick={onClose} className={ctaBtn}>
            Done
          </button>
        </div>
      )}

      {/* Top chrome. */}
      <div className="absolute top-3 right-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {view !== 'auto' && (
          <button
            type="button"
            onClick={() => {
              setInstant(true);
              setPhase(PHASE_END);
              setView('auto');
            }}
            className={ctaBtn}
          >
            <Sparkles className="h-4 w-4" /> Reveal
          </button>
        )}
        {view === 'diagram' && (
          <>
            <button type="button" onClick={exportSvg} className={ctaBtn}>
              <Download className="h-4 w-4" /> SVG
            </button>
            <button type="button" onClick={exportPng} className={ctaBtn}>
              <Download className="h-4 w-4" /> PNG
            </button>
            <button
              type="button"
              onClick={copyVlConfig}
              className={ctaBtn}
              title="Copy this board as a Visual Language 'Living Graph' config — paste into the /visual-language viewer's config editor"
            >
              {copiedConfig ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copiedConfig ? 'Copied' : 'VL config'}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full text-[#9BA5BC] hover:text-white hover:bg-[var(--bg-raised)]/10"
          aria-label="Close reveal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {view === 'auto' && !done && (
        <p className="absolute bottom-3 inset-x-0 text-center text-[11px]" style={{ color: TEXT_TER }}>
          click to skip
        </p>
      )}
    </div>
  );
}

export default WhiteboardRevealOverlay;
